import React, { useRef, useEffect, useCallback, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';

// Modal styles
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '80vh',
    bgcolor: '#16223b', // Dark blue/purple background
    border: '2px solid #373737',
    boxShadow: 24,
    pt: 1,
    px: 1,
    pb: 3,
    color: '#e0e0e0', // Light text for contrast
};

// Editor container styles
const editorStyles = {
    minHeight: '400px', // Match EditBlog for consistency
    border: '1px solid #4a5568',
    padding: '10px',
    background: '#1e2a44', // Slightly darker than modal
    color: '#e0e0e0',
    overflowY: 'auto',
    position: 'relative',
    display: 'block',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

// Override Editor.js styles for dark theme
const editorCSS = `
    .codex-editor {
        background: #1e2a44 !important;
        color: #e0e0e0 !important;
        min-height: 400px !important;
        border-radius: 8px !important;
    }
    .codex-editor__redactor {
        padding-bottom: 100px !important;
        min-height: 400px !important;
    }
    .ce-block__content {
        background: #1e2a44 !important;
        color: #e0e0e0 !important;
    }
    .ce-toolbar {
        background: #2d3748 !important;
        color: #e0e0e0 !important;
        display: flex !important;
        visibility: visible !important;
    }
    .ce-toolbar__content {
        background: #2d3748 !important;
        max-width: 100% !important;
    }
    .ce-inline-tool, .ce-toolbar__actions, .ce-toolbar__plus {
        color: #e0e0e0 !important;
        background: #2d3748 !important;
    }
    .ce-inline-tool:hover, .ce-toolbar__plus:hover {
        background: #4a5568 !important;
    }
    .ce-block a {
        color: #63b3ed !important;
    }
    .ce-header {
        color: #e0e0e0 !important;
    }
    .ce-paragraph {
        color: #e0e0e0 !important;
    }
    .ce-list {
        color: #e0e0e0 !important;
    }
    .ce-block--selected .ce-block__content {
        background: #2b3a55 !important;
    }
    .ce-inline-toolbar {
        background: #2d3748 !important;
        border: 1px solid #4a5568 !important;
        color: #e0e0e0 !important;
    }
    .ce-popover {
        background: #2d3748 !important;
        color: #e0e0e0 !important;
    }
`;

export function NoteModel({ open, setOpen, initialNotesData, handleUpdateNotes }) {
    const editorRef = useRef(null);
    const editorInstance = useRef(null);
    const [isEditorReady, setIsEditorReady] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const initializeEditor = useCallback(() => {
        if (!editorInstance.current && editorRef.current) {
            try {
                let dataToRender;
                if (typeof initialNotesData === 'string') {
                    dataToRender = {
                        blocks: [{
                            type: 'paragraph',
                            data: { text: initialNotesData || 'Enter your notes here...' }
                        }],
                        time: new Date().getTime(),
                        version: '2.31.0'
                    };
                } else {
                    dataToRender = initialNotesData && initialNotesData.blocks ? initialNotesData : { blocks: [], time: new Date().getTime(), version: '2.31.0' };
                }

                console.log('Initializing Editor.js with data:', dataToRender);

                editorInstance.current = new EditorJS({
                    holder: editorRef.current,
                    tools: {
                        header: {
                            class: Header,
                            config: { placeholder: 'Enter a header', levels: [2, 3, 4], defaultLevel: 2 },
                            inlineToolbar: true
                        },
                        list: { class: List, inlineToolbar: true },
                        paragraph: { class: Paragraph, inlineToolbar: true }
                    },
                    data: dataToRender,
                    onChange: () => {
                        console.log('Editor.js content changed.');
                    },
                    placeholder: 'Enter dealer notes/remarks...',
                    onReady: () => {
                        console.log('Editor.js is ready');
                        setIsEditorReady(true);
                    },
                    logLevel: 'ERROR'
                });
            } catch (error) {
                console.error('Editor.js initialization error:', error);
            }
        }
    }, [initialNotesData]);

    useEffect(() => {
        if (open) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.jsdelivr.net/npm/@editorjs/editorjs@2.31.0/dist/editorjs.css';
            document.head.appendChild(link);

            const styleSheet = document.createElement('style');
            styleSheet.innerHTML = editorCSS;
            document.head.appendChild(styleSheet);

            const timeoutId = setTimeout(() => {
                initializeEditor();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                if (editorInstance.current && typeof editorInstance.current.destroy === 'function') {
                    editorInstance.current.destroy();
                    editorInstance.current = null;
                    setIsEditorReady(false);
                    console.log('Editor.js destroyed');
                }
                // Clean up injected styles
                const links = document.head.getElementsByTagName('link');
                for (let i = links.length - 1; i >= 0; i--) {
                    if (links[i].href.includes('editorjs.css')) {
                        document.head.removeChild(links[i]);
                    }
                }
                const styleSheets = document.head.getElementsByTagName('style');
                for (let i = styleSheets.length - 1; i >= 0; i--) {
                    if (styleSheets[i].innerHTML === editorCSS) {
                        document.head.removeChild(styleSheets[i]);
                    }
                }
            };
        }
    }, [open, initializeEditor]);

    const handleSaveAndClose = async () => {
        if (editorInstance.current) {
            try {
                const savedData = await editorInstance.current.save();
                console.log('Editor.js saved data on update:', savedData);
                // Pass the new data back to the parent to handle the update
                handleUpdateNotes(savedData);
                handleClose();
            } catch (error) {
                console.error('Editor.js save error on update:', error);
            }
        } else {
            console.warn('Editor.js instance not initialized');
        }
    };

    return (
        <div>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="parent-modal-title"
                aria-describedby="parent-modal-description"
            >
                <Box sx={style}>
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-1 mx-2 py-4">
                        <div className="breadcrumb-title pe-3" style={{ color: '#e0e0e0' }}>Note-board</div>
                        <div className="ps-3">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb mb-0 p-0">
                                    <li className="breadcrumb-item">
                                        <span style={{ color: '#e0e0e0' }}>Dealer View</span>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page" style={{ color: '#63b3ed' }}>
                                        Dealer remark/notes
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                    <div className="card radius-5" style={{ background: '#1e2a44', borderColor: '#4a5568' }}>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-12 col-lg-12 col-xl-12 d-flex">
                                    <div className="card mb-0 radius-10 border shadow-none w-100" style={{ background: '#1e2a44', borderColor: '#4a5568' }}>
                                        <div className="card-body">
                                            <div className="col-12">
                                                <label className="form-label" style={{ color: '#e0e0e0' }}>Dealer Notes/Remarks</label>
                                                {!isEditorReady && (
                                                    <div style={{ ...editorStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <p style={{ color: '#e0e0e0' }}>Loading editor...</p>
                                                    </div>
                                                )}
                                                <div
                                                    ref={editorRef}
                                                    style={{
                                                        ...editorStyles,
                                                        display: isEditorReady ? 'block' : 'none'
                                                    }}
                                                >
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-header py-3 bg-transparent" style={{ background: '#1e2a44' }}>
                            <div className="d-sm-flex align-items-center">
                                <h5 className="mb-2 mb-sm-0" style={{ color: '#e0e0e0' }}>Update the Notes</h5>
                                <div className="ms-auto">
                                    <button
                                        type="button"
                                        className="btn btn-secondary mx-2"
                                        style={{ background: '#4a5568', color: '#e0e0e0', borderColor: '#4a5568' }}
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={{ background: '#3182ce', color: '#e0e0e0', borderColor: '#3182ce' }}
                                        onClick={handleSaveAndClose}
                                    >
                                        Update
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}